import * as React from 'react';
import { ArrowUpRight } from 'lucide-react';
import type { VariantProps } from 'class-variance-authority';

import { Button, buttonVariants } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { BalanceCard, ApprovalCard } from '@/components/product-mocks';
import { links } from '@/content/site';
import { trackWebsiteEvent } from '@/lib/analytics';

// The hero's rotating point-globe, reused as the MLS card's motion graphic.
// Lazy-loaded (like the hero) so Three.js only loads when the dialog opens.
const HeroScene = React.lazy(() =>
  import('@/components/three/HeroScene').then((m) => ({ default: m.HeroScene }))
);

/* -------------------------------------------------------------------------- */
/*  App chooser content — EDIT HERE to change what the "Open app" CTAs show.  */
/*                                                                            */
/*  Every "Open app" / "Open the app" button across the site opens this same  */
/*  dialog (via OpenAppButton below), so this is the single source of truth   */
/*  for the options, copy, and which one is highlighted.                      */
/* -------------------------------------------------------------------------- */

/**
 * The "which app?" chooser dialog, reused by every OpenAppButton. Both options
 * are inlined below. Den MLS (multi-layer security) is the promoted product and
 * gets a tall card with the hero point-globe motion graphic filling the top;
 * Den Multisig is the legacy Safe-based app and gets a compact secondary button.
 * There's no dialog title — each card's "Open the … app" label says what it does.
 */
export function OpenAppDialog({
  open,
  onClose,
  analyticsLocation,
}: {
  open: boolean;
  onClose: () => void;
  analyticsLocation: string;
}) {
  return (
    // NOTE: `|| true` forces the dialog open for previewing the design — revert
    // this to `open={open}` before shipping.
    <Dialog open={open} onClose={onClose} ariaLabel="Open the Den app">
      {/* mt-7 drops the cards below the panel's close (X) button so it never
          overlaps the first card. */}
      <div className="mt-7 flex flex-col gap-3">
        {/* Den MLS Wallet — promoted product. The hero motion graphic (globe +
            floating Balance/Approval mocks) fills the top of the card; the label
            + description sit at the bottom. */}
        <a
          href={links.mlsApp}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            trackWebsiteEvent('open_app_clicked', {
              app: 'mls',
              destination: links.mlsApp,
              cta_location: analyticsLocation,
            });
            onClose();
          }}
          className="group relative isolate block overflow-hidden rounded-2xl border-2 border-slate-300 active:border-ember-300/70 transition-colors hover:border-ember-200/70"
        >
          {/* Card background — identical to the "active" security LayerCard:
              ember-50 base + a white radial gradient washing in from the
              bottom-right + the masked cap-dots grid (mirrors FLOW.card.activeBg
              + .cap-dots in SecurityLayers.tsx). */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-10 transition group-active:bg-ember-100/40  bg-slate-200/80 group-hover:bg-white bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-white to-transparent"
          >
            <div className="absolute inset-0 isolate" />
          </div>

          {/* Motion graphic: the hero point-globe + floating product mocks,
              rendered over the card background above. */}
          <div aria-hidden className="relative h-56">
            <React.Suspense fallback={null}>
              <HeroScene className="absolute inset-0 opacity-100 transition group-hover:scale-105 [mask-image:radial-gradient(68%_68%_at_50%_44%,black,transparent)]" />
            </React.Suspense>
            {/* Scale + position live on the wrapper; the float animation lives on
                the card itself. They MUST be separate elements: the float's
                `transform: translateY()` would otherwise clobber the wrapper's
                `transform: scale()` (a `scale-*` class on the animated card alone
                has no effect). Tune the scale-[…] here to resize the mocks. */}
            <div className="absolute top-0 bottom-0 right-0 left-0 opacity-90 group-hover:opacity-100 transition scale-100 group-hover:-translate-x-2 group-hover:scale-105">
              <div className="absolute bottom-[20%] right-[23%]  max-w-[230px] origin-bottom-right scale-[0.5]">
                <BalanceCard className="w-full animate-float-slow" />
              </div>
              <div className="absolute bottom-[8%] right-[2%]  max-w-[240px] origin-bottom-right scale-[0.5]">
                <ApprovalCard className="w-full animate-float" />
              </div>
            </div>
          </div>
          {/* Up-right arrow, top-right of the card. */}
          <ArrowUpRight className="absolute right-4 top-4 z-10 size-5 text-slate-500 group-hover:text-ember-600 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          {/* Label + description at the bottom. */}
          <div className="relative px-6 pb-6 pt-1">
            <div className="flex flex-wrap flex-col items-start gap-x-2 gap-y-3">
              <h3 className="text-2xl font-semibold text-slate-900 group-hover:text-slate-600 transition">
                Open MLS Wallet
              </h3>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-800 group-hover:text-slate-500 transition">
              Manage your treasury, run internal operations, and launch digital
              asset products using wallets that protect against the most
              sophisticated hackers.
            </p>
          </div>
        </a>

        {/* Den Multisig — legacy Safe-based app. Compact, secondary. */}
        <a
          href={links.multisigApp}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            trackWebsiteEvent('open_app_clicked', {
              app: 'multisig',
              destination: links.multisigApp,
              cta_location: analyticsLocation,
            });
            onClose();
          }}
          className="group relative block rounded-2xl border border-hairline bg-ink-800 px-6 py-4 pr-11 transition-colors hover:bg-ink-700 active:bg-ink-600"
        >
          <ArrowUpRight className="absolute right-4 top-4 size-4 text-ink-300 transition-colors" />
          <h3 className="text-sm font-medium text-slate-50">
            Open Den Multisig
          </h3>
          <p className="mt-0.5 text-xs leading-snug text-slate-50">
            Multi-signature wallet for crypto-native teams
          </p>
        </a>
      </div>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/*  Trigger                                                                   */
/* -------------------------------------------------------------------------- */

type OpenAppButtonProps = VariantProps<typeof buttonVariants> &
  Omit<React.ComponentPropsWithoutRef<'button'>, 'className'> & {
    className?: string;
    analyticsLocation?: string;
  };

/**
 * Drop-in replacement for the "Open app" CTA `<Button>`: renders a button that
 * opens the {@link OpenAppDialog}. Forwards Button styling (variant/size/
 * className) and any extra onClick (e.g. to also close the mobile menu).
 */
export function OpenAppButton({
  children = 'Open app',
  onClick,
  analyticsLocation = 'content',
  ...props
}: OpenAppButtonProps) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Button
        type="button"
        onClick={(e) => {
          onClick?.(e);
          if (e.defaultPrevented) return;
          trackWebsiteEvent('app_chooser_opened', {
            cta_location: analyticsLocation,
          });
          setOpen(true);
        }}
        {...props}
      >
        {children}
      </Button>
      <OpenAppDialog
        open={open}
        onClose={() => setOpen(false)}
        analyticsLocation={analyticsLocation}
      />
    </>
  );
}
